import { UTCDate } from '@date-fns/utc';
import { EvidenceSource } from '@seapoint/server/enums';
import { createColumnHelper } from '@tanstack/react-table';
import { format, isThisYear, isToday, isTomorrow, isValid, isYesterday } from 'date-fns';
import { EllipsisVertical, FileUp } from 'lucide-react';
import { ComponentProps } from 'react';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Badge } from '../../../../design-system/badge/badge';
import { Button } from '../../../../design-system/button/button';
import { ColumnHeaderWithSorting } from '../../../../design-system/data-table/column-header-with-sorting';
import { Popover } from '../../../../design-system/popover/popover';
import { InvoiceEvidenceView } from '../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { getNormalizedAmount } from '../../../../utils/get-normalized-amount';
import { isSupportedCurrency } from '../../../payments/utils/is-supported-currency';
import { getInvoiceDueDateLabel } from '../../utils/get-invoice-due-date-label';
import { InvoiceTableRowOptionsMenu } from './invoice-table-row-options-menu';
import { getBadgeVariant } from './utils/get-badge-variant';
import { getInvoiceStatusLabel } from './utils/get-invoice-status-label';
import { getJointAvatarDescription } from './utils/get-joint-avatar-description';
import { sortDueDate } from './utils/sort-to-pay-invoice-due-date';

const columnHelper = createColumnHelper<InvoiceEvidenceView>();

export const toPayInvoiceTableColumnsDefinition = [
  columnHelper.accessor(
    (row) => getInvoiceDueDateLabel({ dueDate: row.billingDates?.dueDate, invoiceDate: row.billingDates?.invoiceDate }),
    {
      id: 'dueDate',
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Due Date" />,
      cell: ({ row }) => {
        const cellContent = getInvoiceDateCellContent(
          row.original.billingDates?.dueDate ?? row.original.billingDates?.invoiceDate,
        );

        return <p className="text-sm font-normal leading-6 tracking-tight text-black">{cellContent}</p>;
      },
      sortingFn: sortDueDate,
      size: 12,
      minSize: 12,
    },
  ),
  columnHelper.accessor(
    (row) =>
      row.source === EvidenceSource.UPLOAD
        ? 'Manually uploaded'
        : (row.emailInformation?.fromEmail ?? 'Unknown sender'),
    {
      id: 'receivedFrom',
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Received from" />,
      cell: ({ row }) => {
        const { name, description } = getReceivedFromCellContent(row.original);

        return <AvatarNameDescription avatar={{ showAvatar: false }} name={name} description={description} />;
      },
      size: 23,
    },
  ),
  columnHelper.accessor(
    (row) => row.suggestedBeneficiary?.name ?? row.vendorInformation?.vendorCompanyName ?? 'Unknown',
    {
      id: 'invoiceInfo',
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Invoice info" />,
      cell: ({ row }) => {
        const cellContent =
          row.original.suggestedBeneficiary?.name ?? row.original.vendorInformation?.vendorCompanyName ?? 'Unknown';

        return (
          <AvatarNameDescription
            avatar={{ showAvatar: true }}
            name={cellContent}
            description={row.original.invoiceDetails?.invoiceReference}
            descriptionIcon={<FileUp strokeWidth={1} size={16} color="#68686D" />}
          />
        );
      },
      size: 20,
    },
  ),
  columnHelper.accessor(
    (row) =>
      getInvoiceStatusLabel({
        isPaid: row.isPaid,
        date: row.billingDates?.dueDate ?? row.billingDates?.invoiceDate,
      }),
    {
      id: 'status',
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Status" />,
      cell: ({ row }) => {
        const status = getInvoiceStatusLabel({
          isPaid: row.original.isPaid,
          date: row.original.billingDates?.dueDate ?? row.original.billingDates?.invoiceDate,
        });
        const badgeVariant = getBadgeVariant(status);

        return <Badge variant={badgeVariant}>{status}</Badge>;
      },
      size: 14,
      minSize: 14,
    },
  ),
  columnHelper.accessor(
    (row) => {
      if (
        !row.invoiceDetails?.totalAmount ||
        !row.invoiceDetails?.currency ||
        !isSupportedCurrency(row.invoiceDetails?.currency)
      ) {
        return '—';
      }

      return getNormalizedAmount({
        amount: row.invoiceDetails.totalAmount,
        isCredit: true,
      });
    },
    {
      id: 'amount',
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Amount" />,
      cell: ({ row }) => {
        if (
          !row.original.invoiceDetails?.totalAmount ||
          !row.original.invoiceDetails?.currency ||
          !isSupportedCurrency(row.original.invoiceDetails?.currency)
        ) {
          return <p className="text-end font-normal leading-6 tracking-tight text-black">—</p>;
        }

        const amountValue = getAmountWithCurrency({
          amount: row.original.invoiceDetails?.totalAmount,
          currency: row.original.invoiceDetails?.currency,
          isAccountBalance: true,
        });

        return <p className="text-end font-normal leading-6 tracking-tight text-black">{amountValue}</p>;
      },
      size: 14,
      minSize: 14,
    },
  ),
  columnHelper.display({
    id: 'review_button',
    cell: ({ row, table }) => {
      const isSelected = table.options.meta?.selectedEvidenceId === row.original.id;

      return (
        <Button
          variant="default"
          isLoading={isSelected && table.options.meta?.isFetchingDocumentUrl}
          onClick={() => table.options.meta?.onReviewInvoice?.(row.original.id)}
        >
          View
        </Button>
      );
    },
    size: 10,
    minSize: 10,
  }),
  columnHelper.display({
    id: 'options',
    cell: ({ row, table }) => {
      const menuItems: ComponentProps<typeof InvoiceTableRowOptionsMenu>['menuItems'] = [
        'MARK_PAID',
        'MARK_NOT_INVOICE',
      ];

      if (row.original.emailInformation?.fromEmail && table.options.meta?.isEmailConnected) {
        menuItems.push('EXCLUDE_SENDER');
      }

      return (
        <Popover
          side="bottom"
          align="end"
          triggerElement={<EllipsisVertical strokeWidth={1} size={16} />}
          content={
            <InvoiceTableRowOptionsMenu
              menuItems={menuItems}
              onSelectMenuItem={(menuItemVariant) => {
                table.options.meta?.onSelectMenuItem?.({
                  entityId: row.original.id,
                  menuItemVariant,
                });
              }}
            />
          }
          className="min-w-[100px] overflow-clip"
        />
      );
    },
    size: 5,
    minSize: 5,
  }),
];

export const getInvoiceDateCellContent = (date?: string | Date) => {
  if (!date || !isValid(new UTCDate(date))) {
    return 'Unknown';
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  if (isTomorrow(date)) {
    return 'Tomorrow';
  }

  if (isToday(date)) {
    return 'Today';
  }

  if (isThisYear(date)) {
    return format(date, 'dd MMM');
  }

  return format(date, 'dd MMM yyyy');
};

const getReceivedFromCellContent = (evidence: InvoiceEvidenceView) => {
  const name =
    evidence.source === EvidenceSource.UPLOAD
      ? 'Manually uploaded'
      : (evidence.emailInformation?.fromEmail ?? 'Unknown sender');

  const description = getJointAvatarDescription({
    item1: getInvoiceDateCellContent(evidence.emailInformation?.time),
    item2: evidence.source === EvidenceSource.UPLOAD ? undefined : evidence.emailInformation?.subject,
  });

  return {
    name,
    description,
  };
};
